import { css, TableCell, TableRow } from '@mui/material'
import type { ReactElement, ReactNode } from 'react'

import { colorTokens, spacing, Text, type UiContext } from '@orus.eu/pharaoh'
import type { ColorToken } from '@orus.eu/pharaoh/src/foundations/color-tokens'

type ChangesRowProps = {
  variable: string
  beforeChangeFormattedValue: ReactNode
  beforeChangeColor?: ColorToken | string | undefined
  afterChangeColor?: ColorToken | string | undefined
  afterChangeFormattedValue: ReactNode
  validatedChangeFormattedValue?: ReactNode
  showValidatedColumn?: boolean
  uiContext: UiContext
}

export function ChangesRow({
  variable,
  beforeChangeFormattedValue,
  afterChangeFormattedValue,
  validatedChangeFormattedValue,
  beforeChangeColor,
  afterChangeColor,
  showValidatedColumn = false,
  uiContext,
}: ChangesRowProps): ReactElement {
  function convertNullToDash(value: string): string {
    return value === 'NULL' ? '-' : value
  }
  function convertBooleanToIcon(value: string): string {
    return value.toLowerCase() === 'true' ? '✓' : value.toLowerCase() === 'false' ? '✗' : value
  }

  function formatContent(value: ReactNode): ReactNode {
    if (typeof value !== 'string') {
      return value
    }

    return convertNullToDash(convertBooleanToIcon(value))
  }

  const formatedBefore = formatContent(beforeChangeFormattedValue)
  const formatedAfter = formatContent(afterChangeFormattedValue)
  const formatedValidated =
    showValidatedColumn && validatedChangeFormattedValue !== undefined
      ? formatContent(validatedChangeFormattedValue)
      : undefined

  if (uiContext === 'mobile') {
    return (
      <ChangesRowMobile
        variable={variable}
        beforeChange={formatedBefore}
        beforeChangeColor={beforeChangeColor}
        afterChange={formatedAfter}
        afterChangeColor={afterChangeColor}
        validatedChange={formatedValidated}
      />
    )
  }
  return (
    <ChangesRowDesktop
      variable={variable}
      beforeChange={formatedBefore}
      beforeChangeColor={beforeChangeColor}
      afterChange={formatedAfter}
      afterChangeColor={afterChangeColor}
      validatedChange={formatedValidated}
    />
  )
}

type ChangesRowUiProps = {
  variable: string
  beforeChange: ReactNode
  beforeChangeColor?: ColorToken | string | undefined
  afterChange: ReactNode
  afterChangeColor?: ColorToken | string | undefined
  validatedChange?: ReactNode
}

function ChangesRowDesktop({
  variable,
  beforeChange,
  beforeChangeColor,
  afterChange,
  afterChangeColor,
  validatedChange,
}: ChangesRowUiProps) {
  const beforeChangeColorValue = beforeChangeColor ?? colorTokens['color-text-base-disable']
  const afterChangeColorValue = afterChangeColor ?? colorTokens['color-text-base-basic']
  const validatedChangeColorValue = colorTokens['color-text-base-main']

  const cells = (
    <>
      <TableCell>
        <Text variant="body2SemiBold">{variable}</Text>
      </TableCell>
      <TableCell>
        <Text variant="body2" color={beforeChangeColorValue}>
          {beforeChange}
        </Text>
      </TableCell>
      {validatedChange !== undefined && (
        <TableCell>
          <Text variant="body2" color={validatedChangeColorValue}>
            {validatedChange}
          </Text>
        </TableCell>
      )}
      <TableCell>
        <Text variant="body2" color={afterChangeColorValue}>
          {afterChange}
        </Text>
      </TableCell>
    </>
  )

  return <TableRow>{cells}</TableRow>
}

function ChangesRowMobile({
  variable,
  beforeChange,
  beforeChangeColor,
  afterChange,
  afterChangeColor,
  validatedChange,
}: ChangesRowUiProps) {
  const beforeChangeColorValue = beforeChangeColor ?? colorTokens['color-text-base-disable']
  const afterChangeColorValue = afterChangeColor ?? colorTokens['color-text-base-basic']
  const validatedChangeColorValue = colorTokens['color-text-base-main']

  return (
    <TableRow>
      <TableCell
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing[10]};
        `}
      >
        <Text
          variant="body2SemiBold"
          css={css`
            margin-bottom: ${spacing[50]};
          `}
        >
          {variable}
        </Text>
        <Text variant="body2" color={beforeChangeColorValue}>
          {beforeChange}
        </Text>
        {validatedChange !== undefined && (
          <Text variant="body2" color={validatedChangeColorValue}>
            {validatedChange}
          </Text>
        )}
        <Text variant="body2" color={afterChangeColorValue}>
          {afterChange}
        </Text>
      </TableCell>
    </TableRow>
  )
}
